/**
 * Survey builder constants.
 *
 * The arrays mirror the whitelists declared in `app/models/survey_form.rb` and
 * `app/models/survey_response.rb`. Whenever the model gains a kind, a question
 * type or a status, add it here as well — the builder validates against these
 * lists before the payload ever reaches the API.
 */

export const SURVEY_KINDS = Object.freeze([
  'visit',
  'proposal',
  'conversation',
  'post_sale',
  'support',
  'listing',
  'csat',
  'visit_feedback',
  'kyc',
  'custom',
]);

export const SURVEY_OPERATIONAL_FLOWS = Object.freeze([
  'generic',
  'visit_request',
  'property_offer',
  'kyc_request',
  'listing_intake',
  'buyer_qualification',
  'tenant_qualification',
  'owner_onboarding',
  'inspection',
  'contract_signature',
  'maintenance_request',
  'post_sale_follow_up',
]);

export const LEASE_BOUND_OPERATIONAL_FLOWS = Object.freeze([
  'maintenance_request',
  'inspection',
]);

export const DEFAULT_OPERATIONAL_FLOW_BY_KIND = Object.freeze({
  visit: 'visit_request',
  proposal: 'property_offer',
  kyc: 'kyc_request',
});

export const operationalFlowForKind = kind =>
  DEFAULT_OPERATIONAL_FLOW_BY_KIND[kind] || 'generic';

export const SURVEY_QUESTION_TYPES = Object.freeze([
  'rating',
  'nps',
  'text',
  'long_text',
  'single_choice',
  'multi_choice',
  'boolean',
  'likert',
  'date',
  'time',
  'email',
  'phone',
  'number',
  'document_id',
]);

// Types whose answer is a number the backend aggregates into `score`.
export const SCORED_QUESTION_TYPES = Object.freeze(['rating', 'nps', 'likert']);

export const CHOICE_QUESTION_TYPES = Object.freeze([
  'single_choice',
  'multi_choice',
]);

export const TEXT_QUESTION_TYPES = Object.freeze(['text', 'long_text']);

export const PLACEHOLDER_QUESTION_TYPES = Object.freeze([
  'text',
  'long_text',
  'email',
  'phone',
  'number',
  'document_id',
]);

export const NUMBER_BOUND_QUESTION_TYPES = Object.freeze(['number']);

export const SURVEY_RESPONSE_STATUSES = Object.freeze([
  'pending',
  'sent',
  'opened',
  'submitted',
  'expired',
]);

export const SURVEY_KIND_ICONS = Object.freeze({
  visit: 'i-lucide-door-open',
  proposal: 'i-lucide-file-signature',
  conversation: 'i-lucide-messages-square',
  post_sale: 'i-lucide-handshake',
  support: 'i-lucide-life-buoy',
  listing: 'i-lucide-building-2',
  csat: 'i-lucide-message-square-heart',
  visit_feedback: 'i-lucide-clipboard-check',
  kyc: 'i-lucide-file-lock-2',
  custom: 'i-lucide-sparkles',
});

export const QUESTION_TYPE_ICONS = Object.freeze({
  rating: 'i-lucide-star',
  nps: 'i-lucide-gauge',
  text: 'i-lucide-type',
  long_text: 'i-lucide-align-left',
  single_choice: 'i-lucide-circle-dot',
  multi_choice: 'i-lucide-list-checks',
  boolean: 'i-lucide-toggle-right',
  likert: 'i-lucide-chart-no-axes-column',
  date: 'i-lucide-calendar-days',
  time: 'i-lucide-clock',
  email: 'i-lucide-mail',
  phone: 'i-lucide-phone',
  number: 'i-lucide-calculator',
  document_id: 'i-lucide-id-card',
});

// Canonical tones consumed by LaisStatusBadge / LaisMetricStrip.
export const RESPONSE_STATUS_TONES = Object.freeze({
  pending: 'warning',
  sent: 'info',
  opened: 'info',
  submitted: 'positive',
  expired: 'danger',
});

export const RATING_MAX_OPTIONS = Object.freeze([3, 4, 5, 7, 10]);

// 0 means "no period filter" (all time).
export const RESPONSE_PERIOD_DAYS = Object.freeze([7, 30, 90, 365, 0]);

export const RESPONSES_PER_PAGE = 25;

export const DEFAULT_TEXT_MAX_LENGTH = 500;

// Shared field chrome, so every control in the module looks identical without
// re-declaring the same Tailwind stack in a dozen templates.
export const FIELD_INPUT_CLASS =
  'h-10 w-full rounded-lais-sm border border-lais-border bg-white px-3 text-[14px] text-lais-text transition placeholder:text-lais-text-muted focus:border-lais-primary focus:outline-none focus-visible:shadow-lais-focus disabled:cursor-not-allowed disabled:bg-lais-bg-soft';

export const FIELD_TEXTAREA_CLASS =
  'min-h-[88px] w-full rounded-lais-sm border border-lais-border bg-white px-3 py-2 text-[14px] leading-relaxed text-lais-text transition placeholder:text-lais-text-muted focus:border-lais-primary focus:outline-none focus-visible:shadow-lais-focus';

export const FIELD_SELECT_CLASS =
  'h-10 w-full rounded-lais-sm border border-lais-border bg-white px-3 text-[14px] text-lais-text transition focus:border-lais-primary focus:outline-none focus-visible:shadow-lais-focus';

export const ICON_BUTTON_CLASS =
  'grid size-9 shrink-0 place-items-center rounded-lais border border-lais-border bg-white text-lais-text-muted transition hover:border-lais-primary hover:text-lais-primary focus-visible:outline-none focus-visible:shadow-lais-focus disabled:cursor-not-allowed disabled:opacity-40';

export const SURVEY_ROUTE_NAMES = Object.freeze({
  index: 'settings_surveys_index',
  new: 'settings_surveys_new',
  edit: 'settings_surveys_edit',
  responses: 'settings_surveys_responses',
});
